import React, { useState, useMemo } from 'react';
import { 
  Calendar, 
  FileText, 
  Building2, 
  DollarSign, 
  AlertCircle, 
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Info,
  User,
  Briefcase,
  FileCheck,
  AlertTriangle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ContractDisplayProps {
  data: any;
  className?: string;
}

export const ContractDisplay: React.FC<ContractDisplayProps> = ({ data, className = '' }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    overview: true,
    parties: true,
    financial: true,
    dates: true,
    obligations: false,
    technical: false,
  });

  const [expandedObligations, setExpandedObligations] = useState<Record<number, boolean>>({});

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleObligation = (index: number) => {
    setExpandedObligations(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Calculate data completeness
  const dataCompleteness = useMemo(() => {
    const fields = [
      'contract_type',
      'primary_legal_entity',
      'first_party',
      'second_party',
      'latest_signing_date',
      'contract_amount',
      'contract_scope',
      'obligations'
    ];
    const filledFields = fields.filter(field => data[field] && data[field] !== 'NA');
    return (filledFields.length / fields.length) * 100;
  }, [data]);

  // Get confidence score color
  const getConfidenceColor = (score: number | undefined) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get confidence badge variant
  const getConfidenceBadgeVariant = (score: number | undefined): "default" | "secondary" | "destructive" => {
    if (!score) return 'secondary';
    if (score >= 80) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  // Format date
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr || dateStr === 'NA') return 'Not specified';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Format currency
  const formatCurrency = (amount: string | undefined, currency: string | undefined) => {
    if (!amount || amount === 'NA') return 'Not specified';
    if (amount === 'Variable') return 'Variable pricing';
    
    const currencySymbol = currency || '';
    return `${currencySymbol} ${amount}`;
  };

  const AuditTooltip: React.FC<{ auditText: string | undefined; children: React.ReactNode }> = ({ 
    auditText, 
    children 
  }) => {
    if (!auditText) return <>{children}</>;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted decoration-gray-400">
              {children}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-xs">{auditText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with Data Quality Indicators */}
      <Card className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Contract Analysis</h3>
            </div>
            {data.extraction_confidence_score && (
              <Badge variant={getConfidenceBadgeVariant(data.extraction_confidence_score)}>
                Confidence: {data.extraction_confidence_score}%
              </Badge>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Data Completeness</span>
              <span className="font-medium">{Math.round(dataCompleteness)}%</span>
            </div>
            <Progress value={dataCompleteness} className="h-2" />
          </div>
        </div>
      </Card>

      {/* Contract Overview */}
      <Card className="overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('overview')}
        >
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            <h4 className="font-semibold">Contract Overview</h4>
          </div>
          {expandedSections.overview ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
        
        {expandedSections.overview && (
          <div className="px-4 pb-4 space-y-3">
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Contract Type</label>
                <div className="mt-1">
                  {data.contract_type ? (
                    <AuditTooltip auditText={data.contract_type_audit}>
                      <Badge variant="outline" className="text-sm">
                        {data.contract_type}
                      </Badge>
                    </AuditTooltip>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not specified</span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Primary Legal Entity</label>
                <div className="mt-1">
                  {data.primary_legal_entity ? (
                    <AuditTooltip auditText={data.primary_legal_entity_audit}>
                      <span className="text-sm font-medium">{data.primary_legal_entity}</span>
                    </AuditTooltip>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not specified</span>
                  )}
                </div>
              </div>
            </div>

            {data.contract_subject && (
              <div>
                <label className="text-sm text-muted-foreground">Subject</label>
                <p className="text-sm mt-1">
                  <AuditTooltip auditText={data.contract_subject_audit}>
                    {data.contract_subject}
                  </AuditTooltip>
                </p>
              </div>
            )}

            {data.contract_scope && (
              <div>
                <label className="text-sm text-muted-foreground">Scope</label>
                <p className="text-sm mt-1">
                  <AuditTooltip auditText={data.contract_scope_audit}>
                    {data.contract_scope}
                  </AuditTooltip>
                </p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Parties Section */}
      <Card className="overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('parties')}
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <h4 className="font-semibold">Parties</h4>
          </div>
          {expandedSections.parties ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
        
        {expandedSections.parties && (
          <div className="px-4 pb-4 space-y-3">
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">First Party</label>
                <div className="mt-1">
                  {data.first_party ? (
                    <AuditTooltip auditText={data.first_party_audit}>
                      <span className="text-sm font-medium">{data.first_party}</span>
                    </AuditTooltip>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not specified</span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Second Party</label>
                <div className="mt-1">
                  {data.second_party ? (
                    <AuditTooltip auditText={data.second_party_audit}>
                      <span className="text-sm font-medium">{data.second_party}</span>
                    </AuditTooltip>
                  ) : (
                    <span className="text-sm text-muted-foreground">Not specified</span>
                  )}
                </div>
              </div>
            </div>

            {data.other_party_companies && data.other_party_companies.length > 0 && (
              <div>
                <label className="text-sm text-muted-foreground">Other Parties</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {data.other_party_companies.map((company: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {company}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {data.projects_mentioned && data.projects_mentioned.length > 0 && (
              <div>
                <label className="text-sm text-muted-foreground">Projects Mentioned</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {data.projects_mentioned.map((project: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {project}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Financial Information */}
      <Card className="overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('financial')}
        >
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <h4 className="font-semibold">Financial Information</h4>
          </div>
          {expandedSections.financial ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
        
        {expandedSections.financial && (
          <div className="px-4 pb-4 space-y-3">
            <Separator />
            
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Contract Amount</label>
                <div className="mt-1">
                  <AuditTooltip auditText={data.contract_amount_audit}>
                    <span className="text-lg font-semibold">
                      {formatCurrency(data.contract_amount, data.currency)}
                    </span>
                  </AuditTooltip>
                </div>
              </div>
              
              {data.currency && data.currency !== 'NA' && (
                <div>
                  <label className="text-sm text-muted-foreground">Currency</label>
                  <div className="mt-1">
                    <Badge variant="secondary">{data.currency}</Badge>
                  </div>
                </div>
              )}
            </div>

            {data.has_contingent_liability !== undefined && (
              <div className="flex items-center gap-2">
                {data.has_contingent_liability ? (
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                <span className="text-sm">
                  {data.has_contingent_liability ? 'Has contingent liability' : 'No contingent liability'}
                </span>
              </div>
            )}

            {data.change_of_control_conditions !== undefined && (
              <div className="flex items-center gap-2">
                {data.change_of_control_conditions ? (
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                <span className="text-sm">
                  <AuditTooltip auditText={data.change_of_control_audit}>
                    {data.change_of_control_conditions 
                      ? 'Change of control restrictions apply' 
                      : 'No change of control restrictions'}
                  </AuditTooltip>
                </span>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Important Dates */}
      <Card className="overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('dates')}
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <h4 className="font-semibold">Important Dates</h4>
          </div>
          {expandedSections.dates ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
        
        {expandedSections.dates && (
          <div className="px-4 pb-4 space-y-3">
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Signing Date</label>
                <div className="mt-1">
                  <AuditTooltip auditText={data.latest_signing_date_audit}>
                    <span className="text-sm font-medium">
                      {formatDate(data.latest_signing_date)}
                    </span>
                  </AuditTooltip>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Start Date</label>
                <div className="mt-1">
                  <AuditTooltip auditText={data.contract_start_date_audit}>
                    <span className="text-sm font-medium">
                      {formatDate(data.contract_start_date)}
                    </span>
                  </AuditTooltip>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground">Expiration/Renewal</label>
                <div className="mt-1">
                  <AuditTooltip auditText={data.expiration_renewal_date_audit}>
                    <span className="text-sm font-medium">
                      {formatDate(data.expiration_renewal_date)}
                    </span>
                  </AuditTooltip>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Obligations */}
      {data.obligations && data.obligations.length > 0 && (
        <Card className="overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
            onClick={() => toggleSection('obligations')}
          >
            <div className="flex items-center gap-2">
              <FileCheck className="h-4 w-4" />
              <h4 className="font-semibold">
                Obligations ({data.obligations.length})
              </h4>
            </div>
            {expandedSections.obligations ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
          
          {expandedSections.obligations && (
            <div className="px-4 pb-4">
              <Separator className="mb-4" />
              
              <ScrollArea className="h-96">
                <div className="space-y-3">
                  {data.obligations.map((obligation: any, idx: number) => (
                    <Card key={idx} className="p-3">
                      <div 
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => toggleObligation(idx)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {obligation.obligation_type && (
                              <Badge variant="outline" className="text-xs">
                                {obligation.obligation_type}
                              </Badge>
                            )}
                            {obligation.is_recurring && (
                              <Badge variant="secondary" className="text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                {obligation.recurrence_frequency || 'Recurring'}
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm font-medium">
                            {obligation.clause_title || `Obligation ${idx + 1}`}
                          </p>
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {obligation.clause_number && (
                              <span>Clause: {obligation.clause_number}</span>
                            )}
                            {obligation.amount && obligation.amount !== 'NA' && (
                              <span>Amount: {obligation.amount}</span>
                            )}
                            {obligation.due_date && obligation.due_date !== 'NA' && (
                              <span>Due: {formatDate(obligation.due_date)}</span>
                            )}
                            {obligation.milestone_condition && (
                              <span>Trigger: {obligation.milestone_condition}</span>
                            )}
                          </div>
                        </div>
                        
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {expandedObligations[idx] ? 
                            <ChevronDown className="h-4 w-4" /> : 
                            <ChevronRight className="h-4 w-4" />
                          }
                        </Button>
                      </div>
                      
                      {expandedObligations[idx] && obligation.clause_text && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-1">Clause Text:</p>
                          <p className="text-sm whitespace-pre-wrap">{obligation.clause_text}</p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </Card>
      )}

      {/* Technical Analysis & Data Quality */}
      {(data.technical_analysis || data.extraction_issues || data.data_quality_flags) && (
        <Card className="overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50"
            onClick={() => toggleSection('technical')}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <h4 className="font-semibold">Technical Analysis & Issues</h4>
            </div>
            {expandedSections.technical ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
          
          {expandedSections.technical && (
            <div className="px-4 pb-4 space-y-3">
              <Separator />
              
              {data.technical_analysis && (
                <div>
                  <label className="text-sm text-muted-foreground">Analysis</label>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{data.technical_analysis}</p>
                </div>
              )}
              
              {data.extraction_issues && data.extraction_issues.length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground">Extraction Issues</label>
                  <ul className="mt-1 space-y-1">
                    {data.extraction_issues.map((issue: string, idx: number) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <AlertTriangle className="h-3 w-3 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span>{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {data.data_quality_flags && Object.keys(data.data_quality_flags).length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground">Data Quality Flags</label>
                  <div className="mt-1 space-y-1">
                    {Object.entries(data.data_quality_flags).map(([field, issue]) => (
                      <div key={field} className="text-sm flex items-start gap-2">
                        <Info className="h-3 w-3 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-medium">{field}:</span> {issue as string}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Additional Information */}
      {data.additional_information && data.additional_information !== 'NA' && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4" />
            <h4 className="font-semibold">Additional Information</h4>
          </div>
          <p className="text-sm text-muted-foreground">{data.additional_information}</p>
        </Card>
      )}
    </div>
  );
};

export default ContractDisplay;